// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  partner/src/pages/Onboarding.jsx
//  Copy to: C:\quickry\partner\src\pages\Onboarding.jsx
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  UtensilsCrossed, ShoppingBasket, Package, Pill,
  Check, X, Loader2, ChevronRight, ArrowLeft,
  FileText, CreditCard, AlertCircle, CheckCircle2,
  Upload, Eye, ShieldCheck,
} from "lucide-react";

const API = typeof window !== "undefined" && window.location.hostname !== "localhost"
  ? "https://quickry-ivrv.onrender.com/api"
  : "http://localhost:5000/api";

const C = {
  orange:"#f97316", orangeDark:"#ea580c", orangeLight:"rgba(249,115,22,0.1)",
  black:"#0a0a0a", dark:"rgba(255,255,255,0.04)", white:"#ffffff",
  border:"rgba(255,255,255,0.1)", borderFocus:"#f97316",
  text:"rgba(255,255,255,0.9)", textSub:"rgba(255,255,255,0.55)", textMuted:"rgba(255,255,255,0.3)",
  red:"#ef4444", green:"#4ade80", greenLight:"rgba(22,163,74,0.1)",
};

const TYPES = [
  { val:"restaurant", label:"Restaurant",    sub:"Food delivery",     icon:UtensilsCrossed, color:"#f97316", bg:"rgba(249,115,22,0.12)" },
  { val:"grocery",    label:"Grocery Store", sub:"10-min delivery",   icon:ShoppingBasket,  color:"#16a34a", bg:"rgba(22,163,74,0.12)"  },
  { val:"courier",    label:"Courier",       sub:"Parcel delivery",   icon:Package,         color:"#2563eb", bg:"rgba(37,99,235,0.12)"  },
  { val:"medicine",   label:"Medicine",      sub:"Pharmacy delivery", icon:Pill,            color:"#7c3aed", bg:"rgba(124,58,237,0.12)" },
];

function Field({ label, children, error }) {
  return (
    <div style={{ marginBottom:16 }}>
      <label style={{ display:"block", fontSize:".68rem", fontWeight:700, color:C.textMuted, marginBottom:7, letterSpacing:".5px", textTransform:"uppercase" }}>{label}</label>
      {children}
      {error && (
        <div style={{ color:"#f87171", fontSize:".7rem", marginTop:5, display:"flex", alignItems:"center", gap:4 }}>
          <AlertCircle size={11}/>{error}
        </div>
      )}
    </div>
  );
}

function Input({ value, onChange, placeholder, type="text", maxLength, disabled }) {
  return (
    <input
      type={type} value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder} maxLength={maxLength}
      disabled={disabled}
      style={{ width:"100%", background: disabled ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.06)", border:`1.5px solid ${C.border}`, borderRadius:12, padding:"12px 14px", fontSize:".88rem", color: disabled ? C.textMuted : C.white, fontFamily:"inherit", outline:"none", boxSizing:"border-box", transition:"border-color .2s,box-shadow .2s", cursor: disabled ? "not-allowed" : "text" }}
      onFocus={e => { if(!disabled){ e.target.style.borderColor=C.orange; e.target.style.boxShadow="0 0 0 3px rgba(249,115,22,0.1)"; }}}
      onBlur={e => { e.target.style.borderColor=C.border; e.target.style.boxShadow="none"; }}
    />
  );
}

function Textarea({ value, onChange, placeholder, rows=3 }) {
  return (
    <textarea value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} rows={rows}
      style={{ width:"100%", background:"rgba(255,255,255,0.06)", border:`1.5px solid ${C.border}`, borderRadius:12, padding:"12px 14px", fontSize:".88rem", color:C.white, fontFamily:"inherit", outline:"none", resize:"vertical", boxSizing:"border-box", transition:"border-color .2s" }}
      onFocus={e => { e.target.style.borderColor=C.orange; }}
      onBlur={e => { e.target.style.borderColor=C.border; }}
    />
  );
}

export default function Onboarding() {
  const navigate = useNavigate();
  const { vendor, getToken } = useAuth();

  const [step,    setStep]    = useState(1);
  const [error,   setError]   = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Step 1
  const [bizType, setBizType] = useState("");
  // Step 2
  const [bizName,   setBizName]   = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [phone,     setPhone]     = useState("");
  const [address,   setAddress]   = useState("");
  const [area,      setArea]      = useState("");
  const [pincode,   setPincode]   = useState("");
  const [desc,      setDesc]      = useState("");
  // Step 3
  const [gst,    setGst]    = useState("");
  const [fssai,  setFssai]  = useState("");
  const [pan,    setPan]    = useState("");
  const [aadhar, setAadhar] = useState("");
  // Step 4
  const [acHolder, setAcHolder] = useState("");
  const [acNumber, setAcNumber] = useState("");
  const [ifsc,     setIfsc]     = useState("");
  const [bankName, setBankName] = useState("");
  const [upi,      setUpi]      = useState("");
  // Step 5
  const [files, setFiles] = useState({});
  // Step 6
  const [agreeRead,      setAgreeRead]      = useState(false);
  const [agreeSign,      setAgreeSign]      = useState(false);
  const [signName,       setSignName]       = useState("");
  const [showAgreement,  setShowAgreement]  = useState(false);

  const docRules = {
    restaurant: [
      { key:"fssai",  label:"FSSAI License",         required:true  },
      { key:"gst",    label:"GST Certificate",        required:false },
      { key:"pan",    label:"PAN Card",               required:true  },
      { key:"aadhar", label:"Aadhaar Card",           required:true  },
      { key:"shop",   label:"Shop Address Proof",     required:true  },
    ],
    grocery: [
      { key:"fssai",  label:"FSSAI License",          required:false },
      { key:"gst",    label:"GST Certificate",        required:false },
      { key:"pan",    label:"PAN Card",               required:true  },
      { key:"aadhar", label:"Aadhaar Card",           required:true  },
      { key:"shop",   label:"Shop Address Proof",     required:true  },
    ],
    courier: [
      { key:"gst",    label:"GST Certificate",        required:false },
      { key:"pan",    label:"PAN Card",               required:true  },
      { key:"aadhar", label:"Aadhaar Card",           required:true  },
      { key:"shop",   label:"Business Address Proof", required:true  },
    ],
    medicine: [
      { key:"fssai",  label:"Drug License / FSSAI",   required:true  },
      { key:"gst",    label:"GST Certificate",        required:false },
      { key:"pan",    label:"PAN Card",               required:true  },
      { key:"aadhar", label:"Aadhaar Card",           required:true  },
      { key:"shop",   label:"Pharmacy License",       required:true  },
    ],
  };
  const currentDocs = docRules[bizType] || docRules.restaurant;

  const handleSubmit = async () => {
    // Validations
    if (!agreeRead || !agreeSign) {
      setError("Pehle agreement padho aur dono checkboxes tick karo!"); return;
    }
    if (!signName.trim()) {
      setError("Digital signature ke liye apna poora naam likho!"); return;
    }
    if (!acHolder.trim() || !acNumber.trim() || !ifsc.trim() || !bankName.trim()) {
      setError("Bank details required hain — Step 4 check karo!"); return;
    }

    setLoading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("businessType",    bizType);
      formData.append("businessName",    bizName);
      formData.append("ownerName",       ownerName);
      formData.append("phone",           phone);
      // ✅ FIX: email multiple sources se check karo — undefined na ho
      const vendorEmail = vendor?.email
        || vendor?.vendor?.email
        || vendor?.data?.email
        || "";
      formData.append("email", vendorEmail);
      formData.append("address",         address + (area ? ", " + area : "") + (pincode ? " - " + pincode : ""));
      formData.append("description",     desc);
      formData.append("fssaiNumber",     fssai);
      formData.append("gstNumber",       gst);
      formData.append("panNumber",       pan);
      formData.append("aadharNumber",    aadhar);
      formData.append("bankName",        bankName);
      formData.append("accountNumber",   acNumber);
      formData.append("ifscCode",        ifsc);
      formData.append("accountHolder",   acHolder);
      formData.append("upiId",           upi);
      formData.append("signatureName",   signName);
      formData.append("agreementSigned", "true");

      Object.entries(files).forEach(([key, file]) => {
        if (file) formData.append(key, file);
      });

      const res  = await fetch(`${API}/vendors/apply`, {
        method:  "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body:    formData,
      });

      const data = await res.json();

      if (res.ok && data.success) {
        // SUCCESS — go to dashboard
        setSubmitted(true);
        navigate("/dashboard");
      } else if (
        res.status === 409 ||
        data.message?.toLowerCase().includes("already") ||
        data.message?.toLowerCase().includes("exist")
      ) {
        // Already submitted before — just go to dashboard silently
        navigate("/dashboard");
      } else {
        // Real error — show message, don't navigate
        setError(data.message || "Submission failed! Dobara try karo.");
      }
    } catch (e) {
      console.error("Submit error:", e);
      setError("Network error aaya! Internet connection check karo aur dobara try karo.");
    } finally {
      setLoading(false);
    }
  };

  const goNext = () => {
    setError("");

    if (step === 1) {
      if (!bizType) { setError("Business type select karo"); return; }
    }

    if (step === 2) {
      if (!bizName.trim())              { setError("Business name required hai"); return; }
      if (!ownerName.trim())            { setError("Owner name required hai"); return; }
      if (!/^\d{10}$/.test(phone))      { setError("Valid 10-digit phone number daalo"); return; }
      if (!address.trim())              { setError("Address required hai"); return; }
      if (!area.trim())                 { setError("Area / Locality required hai"); return; }
    }

    if (step === 4) {
      if (!acHolder.trim())             { setError("Account holder name required hai"); return; }
      if (!acNumber.trim())             { setError("Account number required hai"); return; }
      if (!ifsc.trim())                 { setError("IFSC code required hai"); return; }
      if (!bankName.trim())             { setError("Bank name required hai"); return; }
    }

    if (step === 6) {
      handleSubmit();
      return;
    }

    setStep(s => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const STEP_LABELS = ["Business Type", "Details", "Documents", "Bank Details", "Upload Docs", "Agreement"];

  return (
    <div style={{ minHeight:"100vh", background:C.black, color:C.white, fontFamily:"'Plus Jakarta Sans','DM Sans',sans-serif", padding:"24px 16px" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,800;9..144,900&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
        .fu{animation:fadeUp .4s ease both}
        .spin{animation:spin .8s linear infinite}
        input::placeholder,textarea::placeholder{color:rgba(255,255,255,0.25)}
      `}</style>

      <div style={{ maxWidth:580, margin:"0 auto" }}>

        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:28 }}>
          {step > 1 && (
            <button onClick={() => { setStep(s => s - 1); setError(""); }}
              style={{ background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:"50%", width:36, height:36, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", flexShrink:0 }}>
              <ArrowLeft size={16} color="rgba(255,255,255,0.7)"/>
            </button>
          )}
          <div>
            <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.1rem", letterSpacing:"-0.5px" }}>
              Partner Onboarding
            </div>
            <div style={{ fontSize:".72rem", color:C.textMuted, marginTop:2 }}>
              Step {step} of 6 — {STEP_LABELS[step - 1]}
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ display:"flex", gap:6, marginBottom:28 }}>
          {[1,2,3,4,5,6].map(s => (
            <div key={s} style={{ flex:1, height:4, borderRadius:100, background: s <= step ? "linear-gradient(135deg,#ea580c,#f97316)" : "rgba(255,255,255,0.08)", transition:"background .4s" }}/>
          ))}
        </div>

        {/* Step indicators */}
        <div style={{ display:"flex", gap:0, marginBottom:28, overflowX:"auto", scrollbarWidth:"none" }}>
          {STEP_LABELS.map((label, i) => {
            const s      = i + 1;
            const done   = s < step;
            const active = s === step;
            return (
              <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
                <div style={{ width:32, height:32, borderRadius:"50%", background: done ? "rgba(74,222,128,0.15)" : active ? `linear-gradient(135deg,${C.orangeDark},${C.orange})` : "rgba(255,255,255,0.06)", border: done ? "1px solid rgba(74,222,128,0.3)" : active ? "none" : "1px solid rgba(255,255,255,0.1)", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:".78rem", color: done ? "#4ade80" : active ? "#fff" : "rgba(255,255,255,0.3)", transition:"all .3s", boxShadow: active ? "0 4px 14px rgba(249,115,22,0.4)" : "none" }}>
                  {done ? "✓" : s}
                </div>
                <div style={{ fontSize:".58rem", fontWeight:600, color: done ? "#4ade80" : active ? C.orange : "rgba(255,255,255,0.3)", textAlign:"center", whiteSpace:"nowrap" }}>{label}</div>
              </div>
            );
          })}
        </div>

        {/* Error */}
        {error && (
          <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.2)", borderRadius:12, padding:"10px 14px", marginBottom:16, display:"flex", alignItems:"center", gap:8, fontSize:".8rem", color:"#f87171" }}>
            <AlertCircle size={14}/> {error}
          </div>
        )}

        {/* ── STEP 1: Business Type ── */}
        {step === 1 && (
          <div className="fu">
            <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:"1.2rem", letterSpacing:"-0.5px", marginBottom:6 }}>Aap kya deliver karoge?</div>
            <div style={{ fontSize:".82rem", color:C.textSub, marginBottom:22 }}>Apna business type select karo</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:24 }}>
              {TYPES.map(t => {
                const Icon = t.icon;
                const sel  = bizType === t.val;
                return (
                  <div key={t.val} onClick={() => setBizType(t.val)}
                    style={{ background: sel ? "rgba(249,115,22,0.08)" : C.dark, border:`1.5px solid ${sel ? "rgba(249,115,22,0.5)" : C.border}`, borderRadius:16, padding:"18px 14px", cursor:"pointer", transition:"all .2s", display:"flex", alignItems:"center", gap:12 }}>
                    <div style={{ width:44, height:44, borderRadius:12, background:t.bg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      <Icon size={20} color={t.color}/>
                    </div>
                    <div>
                      <div style={{ fontWeight:700, fontSize:".88rem", color: sel ? C.orange : C.text }}>{t.label}</div>
                      <div style={{ fontSize:".7rem", color:C.textMuted, marginTop:2 }}>{t.sub}</div>
                    </div>
                    {sel && <Check size={16} color={C.orange} style={{ marginLeft:"auto", flexShrink:0 }}/>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── STEP 2: Business Details ── */}
        {step === 2 && (
          <div className="fu">
            <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:"1.2rem", letterSpacing:"-0.5px", marginBottom:22 }}>Business Details</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
              <Field label="Business Name *"><Input value={bizName} onChange={setBizName} placeholder="e.g. Hotel Panchavati"/></Field>
              <Field label="Owner Name *"><Input value={ownerName} onChange={setOwnerName} placeholder="Aapka poora naam"/></Field>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
              <Field label="Phone Number *">
                <Input value={phone} onChange={v => setPhone(v.replace(/\D/g,"").slice(0,10))} placeholder="10-digit mobile" maxLength={10}/>
              </Field>
              <Field label="Email (Auto)">
                <Input value={vendor?.email || ""} onChange={() => {}} placeholder="Email" disabled/>
              </Field>
            </div>
            <Field label="Shop / Business Address *"><Input value={address} onChange={setAddress} placeholder="Shop no, street, building"/></Field>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
              <Field label="Area / Locality *"><Input value={area} onChange={setArea} placeholder="e.g. Civil Lines"/></Field>
              <Field label="Pincode">
                <Input value={pincode} onChange={v => setPincode(v.replace(/\D/g,"").slice(0,6))} placeholder="444001" maxLength={6}/>
              </Field>
            </div>
            <Field label="Business Description">
              <Textarea value={desc} onChange={setDesc} placeholder="Apne business ke baare mein batao..."/>
            </Field>
          </div>
        )}

        {/* ── STEP 3: Document Numbers ── */}
        {step === 3 && (
          <div className="fu">
            <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:"1.2rem", letterSpacing:"-0.5px", marginBottom:6 }}>Document Numbers</div>
            <div style={{ fontSize:".78rem", color:C.textSub, marginBottom:22 }}>Document numbers enter karo (files baad mein)</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
              <Field label="GST Number"><Input value={gst} onChange={v => setGst(v.toUpperCase())} placeholder="22AAAAA0000A1Z5" maxLength={15}/></Field>
              <Field label="PAN Number *"><Input value={pan} onChange={v => setPan(v.toUpperCase())} placeholder="ABCDE1234F" maxLength={10}/></Field>
            </div>
            {["restaurant","grocery","medicine"].includes(bizType) && (
              <Field label="FSSAI License Number">
                <Input value={fssai} onChange={setFssai} placeholder="14-digit FSSAI number" maxLength={14}/>
              </Field>
            )}
            <Field label="Aadhaar Number *">
              <Input value={aadhar} onChange={v => setAadhar(v.replace(/\D/g,"").slice(0,12))} placeholder="12-digit Aadhaar" maxLength={12}/>
            </Field>
            <div style={{ background:"rgba(249,115,22,0.06)", border:"1px solid rgba(249,115,22,0.15)", borderRadius:12, padding:"12px 14px", fontSize:".76rem", color:C.textSub, lineHeight:1.6 }}>
              ℹ️ <strong style={{ color:"rgba(255,255,255,0.7)" }}>Next step mein</strong> actual documents (PDF/image) upload karne honge.
            </div>
          </div>
        )}

        {/* ── STEP 4: Bank Details ── */}
        {step === 4 && (
          <div className="fu">
            <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:"1.2rem", letterSpacing:"-0.5px", marginBottom:6 }}>Bank Details</div>
            <div style={{ fontSize:".78rem", color:C.textSub, marginBottom:22 }}>Weekly payment is account mein milega</div>
            <Field label="Account Holder Name *"><Input value={acHolder} onChange={setAcHolder} placeholder="Jaise passbook mein likha ho"/></Field>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
              <Field label="Account Number *"><Input value={acNumber} onChange={setAcNumber} placeholder="Bank account number"/></Field>
              <Field label="IFSC Code *"><Input value={ifsc} onChange={v => setIfsc(v.toUpperCase())} placeholder="SBIN0001234" maxLength={11}/></Field>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
              <Field label="Bank Name *"><Input value={bankName} onChange={setBankName} placeholder="State Bank of India"/></Field>
              <Field label="UPI ID (Optional)"><Input value={upi} onChange={setUpi} placeholder="name@upi"/></Field>
            </div>
            <div style={{ background:"rgba(74,222,128,0.06)", border:"1px solid rgba(74,222,128,0.15)", borderRadius:12, padding:"12px 14px", fontSize:".76rem", color:C.textSub, lineHeight:1.6 }}>
              🔒 <strong style={{ color:"rgba(255,255,255,0.7)" }}>100% Secure & Encrypted.</strong> Sirf weekly settlements ke liye use hoti hai.
            </div>
          </div>
        )}

        {/* ── STEP 5: Document Upload ── */}
        {step === 5 && (
          <div className="fu">
            <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:"1.2rem", letterSpacing:"-0.5px", marginBottom:6 }}>Documents Upload</div>
            <div style={{ fontSize:".78rem", color:C.textSub, marginBottom:10 }}>Original documents ki clear photo ya PDF upload karo</div>
            <div style={{ background:"rgba(249,115,22,0.06)", border:"1px solid rgba(249,115,22,0.15)", borderRadius:12, padding:"10px 14px", fontSize:".74rem", color:C.textSub, lineHeight:1.6, marginBottom:20 }}>
              <strong style={{ color:"rgba(255,255,255,0.7)" }}>Accepted:</strong> PDF, JPG, JPEG, PNG — Max 5MB each.
              {(bizType === "restaurant" || bizType === "medicine") && (
                <><br/><strong style={{ color:"#f87171" }}>FSSAI license mandatory</strong> hai is category mein.</>
              )}
            </div>

            {currentDocs.map(doc => (
              <div key={doc.key} style={{ marginBottom:16 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                  <div style={{ fontSize:".72rem", fontWeight:700, color:C.text }}>{doc.label}</div>
                  {doc.required
                    ? <span style={{ background:"rgba(239,68,68,0.12)", color:"#f87171", fontSize:".58rem", fontWeight:700, padding:"2px 7px", borderRadius:100 }}>REQUIRED</span>
                    : <span style={{ background:"rgba(74,222,128,0.1)", color:"#4ade80", fontSize:".58rem", fontWeight:700, padding:"2px 7px", borderRadius:100 }}>OPTIONAL</span>
                  }
                </div>
                {files[doc.key] ? (
                  <div style={{ background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.2)", borderRadius:10, padding:"11px 14px", display:"flex", alignItems:"center", gap:10 }}>
                    <CheckCircle2 size={16} color="#4ade80" style={{ flexShrink:0 }}/>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:600, fontSize:".8rem", color:C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{files[doc.key].name}</div>
                      <div style={{ fontSize:".66rem", color:C.textMuted }}>{(files[doc.key].size/1024).toFixed(0)} KB</div>
                    </div>
                    <button onClick={() => setFiles(p => { const n = {...p}; delete n[doc.key]; return n; })}
                      style={{ background:"none", border:"none", cursor:"pointer", color:"#f87171", flexShrink:0 }}>
                      <X size={14}/>
                    </button>
                  </div>
                ) : (
                  <label style={{ display:"block", cursor:"pointer" }}>
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display:"none" }}
                      onChange={e => { if(e.target.files[0]) setFiles(p => ({...p, [doc.key]: e.target.files[0]})); }}/>
                    <div style={{ border:`2px dashed ${doc.required ? "rgba(249,115,22,0.4)" : "rgba(255,255,255,0.12)"}`, borderRadius:10, padding:"16px", textAlign:"center", background: doc.required ? "rgba(249,115,22,0.04)" : C.dark, transition:"all .2s" }}>
                      <Upload size={20} color={doc.required ? C.orange : "rgba(255,255,255,0.3)"} style={{ margin:"0 auto 7px", display:"block" }}/>
                      <div style={{ fontWeight:600, fontSize:".78rem", color: doc.required ? C.orange : "rgba(255,255,255,0.4)" }}>Click to upload</div>
                      <div style={{ fontSize:".65rem", color:C.textMuted, marginTop:3 }}>PDF, JPG, PNG — Max 5MB</div>
                    </div>
                  </label>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── STEP 6: Agreement ── */}
        {step === 6 && (
          <div className="fu">
            <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:"1.2rem", letterSpacing:"-0.5px", marginBottom:6 }}>Vendor Agreement</div>
            <div style={{ fontSize:".78rem", color:C.textSub, marginBottom:20 }}>Agreement padho aur digital sign karo</div>

            <div style={{ border:"1px solid rgba(249,115,22,0.25)", borderRadius:14, overflow:"hidden", marginBottom:16 }}>
              <div style={{ background:"linear-gradient(135deg,rgba(234,88,12,0.9),rgba(249,115,22,0.9))", padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <FileText size={18} color="#fff"/>
                  <div>
                    <div style={{ fontWeight:700, fontSize:".88rem", color:"#fff" }}>Quickry Vendor Service Agreement</div>
                    <div style={{ fontSize:".65rem", color:"rgba(255,255,255,0.8)" }}>21 Clauses · Indian Law · IT Act 2000</div>
                  </div>
                </div>
                <button onClick={() => setShowAgreement(s => !s)}
                  style={{ background:"rgba(255,255,255,0.2)", border:"none", borderRadius:8, padding:"6px 12px", color:"#fff", fontWeight:700, fontSize:".72rem", cursor:"pointer", display:"flex", alignItems:"center", gap:5 }}>
                  <Eye size={12}/> {showAgreement ? "Hide" : "Read"}
                </button>
              </div>

              {showAgreement && (
                <div style={{ maxHeight:360, overflowY:"auto", padding:"16px", fontSize:".76rem", lineHeight:1.7, color:C.textSub, background:"rgba(255,255,255,0.03)" }}>
                  <p style={{ fontWeight:800, marginBottom:10, fontSize:".86rem", color:C.text }}>QUICKRY VENDOR SERVICE AGREEMENT</p>
                  <p style={{ marginBottom:12 }}>Agreement between <strong style={{ color:C.text }}>Suhaniya Venture</strong> (Quickry), Near Khandelwal School, Old City, Dabki Road, Akola, Maharashtra – 444002 (UDYAM-MH-02-0046405), represented by <strong style={{ color:C.text }}>Rushikesh Dilip Adhav</strong>, and the Vendor.</p>
                  {[
                    ["1. Definitions","Platform = Quickry digital marketplace (website, app, systems). Customer = order-placing user. Order = confirmed purchase request."],
                    ["2. Purpose","Vendor lists/sells products through Quickry. Quickry provides technology infrastructure. Non-exclusive arrangement."],
                    ["3. Nature of Relationship","Vendor is independent entity. No employment/partnership/joint venture. Quickry is technology intermediary only."],
                    ["4. Vendor Registration","Accurate info required. Documents: Aadhaar, PAN, Bank details, Shop proof, GST, FSSAI (food vendors). Quickry may verify/reject."],
                    ["5. Product Listing","Vendor responsible for accurate listings. Quickry may remove non-compliant listings."],
                    ["6. Product Quality","Vendor solely responsible for quality, safety, hygiene, packaging, legality. Quickry not liable for product issues."],
                    ["7. Order Management","Vendor must promptly accept orders, prepare within time, fulfill accurately, minimize cancellations."],
                    ["8. Delivery","Quickry may facilitate delivery via partner network. Vendor must cooperate with pickup personnel."],
                    ["9. Commission & Fees","18% commission per order (restaurant). Deducted before settlement. Quickry may revise with notice."],
                    ["10. Payment Settlement","Weekly/daily settlements. Quickry may hold payments for disputes or fraud. Refunds deducted from settlements."],
                    ["11. Cancellation & Refund","Vendor liable for refunds due to incorrect/defective orders. Quickry decisions are final."],
                    ["12. Customer Complaints","Vendor must cooperate in resolving complaints. Repeated complaints may lead to suspension."],
                    ["13. Prohibited Activities","No fake reviews, counterfeit products, platform manipulation, or fraudulent activities."],
                    ["14. Intellectual Property","Quickry retains all platform IP. Vendor grants Quickry license to use product images for promotion."],
                    ["15. Confidentiality","Both parties must maintain confidentiality of customer data and business information."],
                    ["16. Indemnification","Vendor indemnifies Quickry against all claims from vendor products, services, or violations."],
                    ["17. Termination","7 days written notice required. Immediate termination for fraud or serious violations."],
                    ["18. Limitation of Liability","Quickry liability limited to commission earned on specific transaction giving rise to claim."],
                    ["19. Governing Law","Governed by laws of India. Disputes in courts of Akola, Maharashtra."],
                    ["20. Electronic Execution","Electronic signatures legally valid under IT Act 2000, same as physical signature."],
                    ["21. Entire Agreement","This Agreement supersedes all prior discussions. Continued use = acceptance of amendments."],
                  ].map(([title, text]) => (
                    <div key={title} style={{ marginBottom:10 }}>
                      <div style={{ fontWeight:700, color:C.orange, marginBottom:3 }}>{title}</div>
                      <p>{text}</p>
                    </div>
                  ))}
                  <div style={{ borderTop:"1px solid rgba(255,255,255,0.08)", marginTop:14, paddingTop:10, color:"rgba(255,255,255,0.4)", fontSize:".7rem" }}>
                    For Quickry: Rushikesh Dilip Adhav · Founder · Suhaniya Venture · Akola, MH 444002
                  </div>
                </div>
              )}
            </div>

            {/* Checkbox 1 */}
            <div onClick={() => setAgreeRead(s => !s)}
              style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"14px", background: agreeRead ? "rgba(74,222,128,0.06)" : "rgba(255,255,255,0.03)", border:`1px solid ${agreeRead ? "rgba(74,222,128,0.25)" : "rgba(255,255,255,0.1)"}`, borderRadius:12, cursor:"pointer", marginBottom:10, transition:"all .2s" }}>
              <div style={{ width:20, height:20, borderRadius:6, border:`2px solid ${agreeRead ? "#4ade80" : "rgba(255,255,255,0.2)"}`, background: agreeRead ? "#4ade80" : "transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:1 }}>
                {agreeRead && <Check size={11} color="#000" strokeWidth={3}/>}
              </div>
              <div style={{ fontSize:".82rem", color:C.text, lineHeight:1.5 }}>
                Maine upar diya hua <strong style={{ color:C.orange }}>Quickry Vendor Service Agreement</strong> padh liya hai aur samajh liya hai
              </div>
            </div>

            {/* Checkbox 2 */}
            <div onClick={() => setAgreeSign(s => !s)}
              style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"14px", background: agreeSign ? "rgba(74,222,128,0.06)" : "rgba(255,255,255,0.03)", border:`1px solid ${agreeSign ? "rgba(74,222,128,0.25)" : "rgba(255,255,255,0.1)"}`, borderRadius:12, cursor:"pointer", marginBottom:16, transition:"all .2s" }}>
              <div style={{ width:20, height:20, borderRadius:6, border:`2px solid ${agreeSign ? "#4ade80" : "rgba(255,255,255,0.2)"}`, background: agreeSign ? "#4ade80" : "transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:1 }}>
                {agreeSign && <Check size={11} color="#000" strokeWidth={3}/>}
              </div>
              <div style={{ fontSize:".82rem", color:C.text, lineHeight:1.5 }}>
                Main is Agreement ki <strong style={{ color:C.orange }}>sabhi terms</strong> se agree karta/karti hoon — yeh legally binding hai
              </div>
            </div>

            {/* Digital Signature */}
            <div>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
                <ShieldCheck size={13} color={C.orange}/>
                <div style={{ fontSize:".68rem", fontWeight:700, color:C.textMuted, textTransform:"uppercase", letterSpacing:".5px" }}>Digital Signature — Apna Poora Naam Likho *</div>
              </div>
              <input value={signName} onChange={e => setSignName(e.target.value)} placeholder="e.g. Rahul Sharma"
                style={{ width:"100%", background:"rgba(255,255,255,0.06)", border:`1.5px solid ${C.border}`, borderRadius:12, padding:"12px 14px", fontSize:"1rem", fontStyle:"italic", fontFamily:"Georgia,serif", color:C.white, outline:"none", boxSizing:"border-box" }}
                onFocus={e => { e.target.style.borderColor=C.orange; e.target.style.boxShadow="0 0 0 3px rgba(249,115,22,0.1)"; }}
                onBlur={e => { e.target.style.borderColor=C.border; e.target.style.boxShadow="none"; }}/>
              <div style={{ fontSize:".67rem", color:C.textMuted, marginTop:6 }}>
                IT Act 2000 ke under legally valid digital signature. Date: {new Date().toLocaleDateString("en-IN")}
              </div>
            </div>
          </div>
        )}

        {/* Next / Submit Button */}
        <button
          onClick={goNext}
          disabled={loading || submitted}
          style={{ width:"100%", marginTop:24, background: submitted ? "rgba(74,222,128,0.2)" : `linear-gradient(135deg,${C.orangeDark},${C.orange})`, border:"none", borderRadius:14, padding:"15px", color:"#fff", fontWeight:800, fontSize:"1rem", cursor: (loading || submitted) ? "not-allowed" : "pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8, boxShadow: submitted ? "none" : "0 6px 24px rgba(249,115,22,0.3)", transition:"all .25s", fontFamily:"inherit", opacity: loading ? 0.8 : 1 }}>
          {submitted
            ? <><CheckCircle2 size={18} color="#4ade80"/> Submitted!</>
            : loading
              ? <><Loader2 size={18} className="spin"/> Submitting...</>
              : step < 6
                ? <>Continue <ChevronRight size={18}/></>
                : <>Submit Application 🚀</>
          }
        </button>

        {step < 6 && !submitted && (
          <div style={{ textAlign:"center", marginTop:12, fontSize:".72rem", color:C.textMuted }}>
            Step {step} of 6 — Almost there!
          </div>
        )}

      </div>
    </div>
  );
}